import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { CloseIcon } from 'assets/icons';
import { PrimaryButton, SecondaryButton } from 'components/forms/atoms/FormButtons';
import { useFeedback } from 'contexts/FeedbackContext';
import moment from 'moment';
import { useState } from 'react';
import { useCreateAppointmentMutation } from 'services/api/appointments.api';
import {
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_TYPE_LABELS,
  APPOINTMENT_TYPES,
  type AppointmentStatus,
  type AppointmentType,
} from 'services/types/appointment';
import { GLASS_BACKGROUND, GLASS_BLUR, NAVY, RADIUS_CARD, RADIUS_ITEM, SHADOW, SURFACE_TINT } from 'styles/tokens';

const fieldSx = {
  '& .MuiFilledInput-root': {
    backgroundColor: SURFACE_TINT,
    borderRadius: '8px',
    overflow: 'hidden',
    '&:hover': { backgroundColor: 'rgba(21, 24, 58, 0.05)' },
    '&.Mui-focused': { backgroundColor: 'rgba(21, 24, 58, 0.05)' },
    '&::before, &::after': { display: 'none' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(21, 24, 58, 0.6)' },
} as const;

interface Props {
  open: boolean;
  onClose: () => void;
  businessUnitId: number;
  branches: string[];
  defaultBranch: string;
}

const NewAppointmentModal = ({ open, onClose, businessUnitId, branches, defaultBranch }: Props) => {
  const { notifySaved } = useFeedback();
  const [createAppointment, { isLoading }] = useCreateAppointmentMutation();

  const [advisorName, setAdvisorName] = useState('');
  const [branchOffice, setBranchOffice] = useState(defaultBranch);
  const [date, setDate] = useState(moment().hour(9).minute(0).format('YYYY-MM-DDTHH:mm'));
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [type, setType] = useState<AppointmentType>('mantencion');
  const [status, setStatus] = useState<AppointmentStatus>('pending');
  const [maintenanceInterval, setMaintenanceInterval] = useState('');
  const [notes, setNotes] = useState('');

  const reset = () => {
    setAdvisorName('');
    setBranchOffice(defaultBranch);
    setDate(moment().hour(9).minute(0).format('YYYY-MM-DDTHH:mm'));
    setDurationMinutes(60);
    setType('mantencion');
    setStatus('pending');
    setMaintenanceInterval('');
    setNotes('');
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!branchOffice || !date) return;
    await createAppointment({
      businessUnitId,
      branchOffice,
      date: moment(date).toISOString(),
      durationMinutes,
      type,
      status,
      advisorName: advisorName.trim() || null,
      maintenanceInterval: maintenanceInterval.trim() || null,
      notes: notes.trim() || null,
    }).unwrap();
    notifySaved('Cita creada');
    close();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            backdropFilter: GLASS_BLUR,
            backgroundColor: GLASS_BACKGROUND,
            boxShadow: SHADOW,
            borderRadius: RADIUS_CARD,
            p: 3,
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: NAVY }}>Nueva cita</Typography>
        <IconButton onClick={close} size="small" sx={{ color: 'rgba(21, 24, 58, 0.5)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Ejecutivo"
          value={advisorName}
          onChange={(e) => setAdvisorName(e.target.value)}
          variant="filled"
          size="small"
          fullWidth
          sx={fieldSx}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            select
            label="Sucursal"
            value={branchOffice}
            onChange={(e) => setBranchOffice(e.target.value)}
            variant="filled"
            size="small"
            fullWidth
            sx={fieldSx}
          >
            {branches.map((branch) => (
              <MenuItem key={branch} value={branch}>
                {branch}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Tipo"
            value={type}
            onChange={(e) => setType(e.target.value as AppointmentType)}
            variant="filled"
            size="small"
            fullWidth
            sx={fieldSx}
          >
            {APPOINTMENT_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {APPOINTMENT_TYPE_LABELS[t]}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Fecha y hora"
            type="datetime-local"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            variant="filled"
            size="small"
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            sx={fieldSx}
          />
          <TextField
            label="Duración (min)"
            type="number"
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(Number(e.target.value))}
            variant="filled"
            size="small"
            sx={{ ...fieldSx, width: 160 }}
          />
        </Box>

        <TextField
          select
          label="Estado"
          value={status}
          onChange={(e) => setStatus(e.target.value as AppointmentStatus)}
          variant="filled"
          size="small"
          fullWidth
          sx={fieldSx}
        >
          {(['pending', 'done'] as AppointmentStatus[]).map((s) => (
            <MenuItem key={s} value={s}>
              {APPOINTMENT_STATUS_LABELS[s]}
            </MenuItem>
          ))}
        </TextField>

        {type === 'mantencion' && (
          <TextField
            label="Intervalo de mantención"
            value={maintenanceInterval}
            onChange={(e) => setMaintenanceInterval(e.target.value)}
            variant="filled"
            size="small"
            fullWidth
            placeholder="Ej: 10.000 km"
            sx={fieldSx}
          />
        )}

        <TextField
          label="Notas"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          variant="filled"
          size="small"
          fullWidth
          multiline
          minRows={2}
          sx={fieldSx}
        />
      </Box>

      <Typography
        sx={{
          mt: 2.5,
          px: 1.5,
          py: 1,
          fontSize: 12,
          lineHeight: 1.4,
          textAlign: 'center',
          color: 'rgba(21, 24, 58, 0.55)',
          backgroundColor: SURFACE_TINT,
          borderRadius: RADIUS_ITEM,
        }}
      >
        Esta cita se creará únicamente en Relif.
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
        <SecondaryButton onClick={close}>Cancelar</SecondaryButton>
        <PrimaryButton onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? 'Guardando…' : 'Crear cita'}
        </PrimaryButton>
      </Box>
    </Dialog>
  );
};

export default NewAppointmentModal;
